import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";

// @ts-ignore - JS service（型定義なし）
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../components/block/TitleBlock";
import HelpHint from "../../components/help-hint/HelpHint";
import SvgLoadingAnimation from "../../components/loading";
import Button from "../../components/button/Button";
import IconButton from "../../components/button/IconButton";
import Input from "../../components/form-item/Input";
import Select from "../../components/form-item/Select";
import ConfirmModal from "../../components/modal/ConfirmModal";
import ContentTagInput from "../../components/content-tag/ContentTagInput";
import Table, { TableColumn } from "../../components/table/Table";
import ToolTip from "../../components/tooltip/ToolTip";
import {
  GalleryTagGroup,
  GalleryTagSource,
  useCreateGalleryTagGroupMutation,
  useDeleteGalleryTagGroupMutation,
  useGetGalleryTagGroupsQuery,
  useReorderGalleryTagGroupsMutation,
  useUpdateGalleryTagGroupMutation,
} from "../../app/services/adminGalleryTags";

// タグ表を編集しやすいよう幅制限なしの全幅レイアウト
const PageWrapper = styled.div.attrs({
  className: "u-col u-w100 u-gap24",
})`
  padding: 24px 16px 80px;
`;

const Section = styled.section.attrs({
  className: "u-col u-w100 u-gap12",
})``;

const TagChipList = styled.div.attrs({
  className: "u-row u-w100 u-gap8",
})`
  flex-wrap: wrap;
`;

const UnmappedChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid var(--soremo-border);
  border-radius: 14px;
  background: transparent;
  cursor: pointer;

  &:hover {
    border-color: var(--soremo-blue);
    background: var(--soremo-bg-blue, rgba(0, 154, 206, 0.06));
  }
`;

// D&D 中にカーソルへ追従するゴースト表示（FileBrowser と同型）
const DragGhost = styled.div.attrs({ className: "bodytext-13" })`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: #fff;
  border: 1px solid var(--soremo-blue);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  white-space: nowrap;

  .material-symbols-rounded {
    font-size: 18px;
    color: var(--soremo-blue);
  }
`;

/** セル内インライン編集用の枠なし input（Table の td:focus-within リングに任せる）。 */
const NAME_INPUT_STYLE: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "1px 6px",
  fontSize: 13,
  lineHeight: "150%",
  height: 24,
  border: "none",
  outline: "none",
  boxShadow: "none",
  background: "transparent",
};

/** 1 階層ぶんのインデント幅(px)。 */
const INDENT_PER_DEPTH = 16;

/** 未割当タグの出どころ表示（tag はアイコンなし = 実タグ）。 */
const UNMAPPED_SOURCE_META: Partial<
  Record<GalleryTagSource, { icon: string; label: string }>
> = {
  creator_title: { icon: "badge", label: "肩書き" },
  creator_name: { icon: "person", label: "アーティスト名" },
  work_title: { icon: "library_music", label: "作品名" },
};

/** API エラーからユーザー向けメッセージを取り出す。 */
const errorMessage = (err: unknown, fallback: string): string =>
  (err as { data?: { message?: string } })?.data?.message ?? fallback;

// =====================================================================
// ツリー構造ヘルパー（親 → 子 → 孫… の走査順を配列で扱う）
// =====================================================================

/** 親 id（最上位は null）→ 子グループ配列。配列内の順序 = 表示順。 */
const buildChildrenMap = (
  list: GalleryTagGroup[],
): Map<number | null, GalleryTagGroup[]> => {
  const ids = new Set(list.map((g) => g.id));
  const map = new Map<number | null, GalleryTagGroup[]>();
  list.forEach((g) => {
    // 親が見つからない迷子行は最上位として表示する
    const key =
      g.parent_id !== null && ids.has(g.parent_id) ? g.parent_id : null;
    map.set(key, [...(map.get(key) ?? []), g]);
  });
  return map;
};

/**
 * サーバーの flat なグループ一覧（display_order, name 順）を
 * 親 → 子 → 孫… の走査順（深さ優先）に並べ替える。循環があっても
 * 無限ループしない（未訪問の残りは末尾に付ける）。
 */
const toTraversalOrder = (list: GalleryTagGroup[]): GalleryTagGroup[] => {
  const childrenMap = buildChildrenMap(list);
  const result: GalleryTagGroup[] = [];
  const seen = new Set<number>();
  const walk = (group: GalleryTagGroup) => {
    if (seen.has(group.id)) return;
    seen.add(group.id);
    result.push(group);
    (childrenMap.get(group.id) ?? []).forEach(walk);
  };
  (childrenMap.get(null) ?? []).forEach(walk);
  list.forEach((g) => {
    if (!seen.has(g.id)) result.push(g);
  });
  return result;
};

/** group 自身と配下（子・孫…）の id 集合（D&D の循環防止に使う）。 */
const collectSubtreeIds = (
  rootId: number,
  childrenMap: Map<number | null, GalleryTagGroup[]>,
): Set<number> => {
  const ids = new Set<number>();
  const stack = [rootId];
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (ids.has(current)) continue;
    ids.add(current);
    (childrenMap.get(current) ?? []).forEach((child) => stack.push(child.id));
  }
  return ids;
};

/** 表示 1 行分（深さと子の有無つき）。 */
interface FlatTagRow {
  group: GalleryTagGroup;
  depth: number;
  hasChildren: boolean;
}

/** 走査順の配列を、折りたたみ状態を反映した表示行リストに変換する。 */
const flattenVisibleRows = (
  ordered: GalleryTagGroup[],
  collapsedIds: Set<number>,
): FlatTagRow[] => {
  const childrenMap = buildChildrenMap(ordered);
  const rows: FlatTagRow[] = [];
  const walk = (group: GalleryTagGroup, depth: number) => {
    const children = childrenMap.get(group.id) ?? [];
    rows.push({ group, depth, hasChildren: children.length > 0 });
    if (collapsedIds.has(group.id)) return;
    children.forEach((child) => walk(child, depth + 1));
  };
  (childrenMap.get(null) ?? []).forEach((g) => walk(g, 0));
  return rows;
};

/**
 * D&D の移動を適用した新しい走査順配列を返す。
 * 循環になる移動（自分自身・自分の子孫の配下へ）は null。
 *
 * @param beforeId 挿入先レベル内でこの id の直前に入れる（null = 末尾）
 */
const applyTreeMove = (
  ordered: GalleryTagGroup[],
  dragId: number,
  newParentId: number | null,
  beforeId: number | null,
): GalleryTagGroup[] | null => {
  const childrenMap = buildChildrenMap(ordered);
  const subtreeIds = collectSubtreeIds(dragId, childrenMap);
  if (newParentId !== null && subtreeIds.has(newParentId)) return null;

  // 親 id → 子 id リストへ落として、dragId を旧位置から新位置へ差し替える
  const childIdsByParent = new Map<number | null, number[]>();
  childrenMap.forEach((children, parentId) => {
    childIdsByParent.set(
      parentId,
      children.map((c) => c.id).filter((id) => id !== dragId),
    );
  });
  const siblings = childIdsByParent.get(newParentId) ?? [];
  let insertIndex = siblings.length;
  if (beforeId !== null && beforeId !== dragId) {
    const idx = siblings.indexOf(beforeId);
    if (idx >= 0) insertIndex = idx;
  }
  childIdsByParent.set(newParentId, [
    ...siblings.slice(0, insertIndex),
    dragId,
    ...siblings.slice(insertIndex),
  ]);

  const byId = new Map(ordered.map((g) => [g.id, g]));
  const result: GalleryTagGroup[] = [];
  const walk = (id: number, parentId: number | null) => {
    const group = byId.get(id);
    if (!group) return;
    result.push(
      group.parent_id === parentId ? group : { ...group, parent_id: parentId },
    );
    (childIdsByParent.get(id) ?? []).forEach((childId) => walk(childId, id));
  };
  (childIdsByParent.get(null) ?? []).forEach((id) => walk(id, null));
  // 迷子（本来発生しない）はそのまま末尾へ
  ordered.forEach((g) => {
    if (!result.some((r) => r.id === g.id)) result.push(g);
  });
  return result;
};

// =====================================================================
// D&D（FileBrowser と同じ useDraggable + useDroppable 方式）
// =====================================================================

/** ドラッグ中のタググループ（excludedIds = 自分と配下。循環防止）。 */
interface ActiveTagDrag {
  group: GalleryTagGroup;
  excludedIds: Set<number>;
}

/** ドロップ先: 行の上 = その行の子にする。 */
interface RowDropData {
  type: "row";
  groupId: number;
}

/** ドロップ先: 行間 = そのレベルへ並び替え挿入。 */
interface GapDropData {
  type: "gap";
  parentId: number | null;
  /** このグループの直前へ挿入（null = レベル末尾）。 */
  beforeId: number | null;
}

type TagDropData = RowDropData | GapDropData;

/**
 * 行間の挿入インジケータ（ドラッグ中のみ描画される薄い行）。
 * ここへのドロップは「同じレベルでの並び替え / そのレベルへの移動」。
 */
const GapDropRow: React.FC<{
  gapId: string;
  parentId: number | null;
  beforeId: number | null;
  depth: number;
  disabled: boolean;
  columnCount: number;
}> = ({ gapId, parentId, beforeId, depth, disabled, columnCount }) => {
  const data = useMemo<GapDropData>(
    () => ({ type: "gap", parentId, beforeId }),
    [parentId, beforeId],
  );
  const { setNodeRef, isOver } = useDroppable({ id: gapId, data, disabled });
  return (
    <tr ref={setNodeRef} style={{ background: "transparent" }}>
      <td
        colSpan={columnCount}
        style={{ padding: 0, height: 10, background: "transparent" }}
      >
        <div
          style={{
            height: 3,
            margin: `3px 8px 4px ${44 + depth * INDENT_PER_DEPTH}px`,
            borderRadius: 2,
            background:
              isOver && !disabled ? "var(--soremo-blue)" : "transparent",
          }}
        />
      </td>
    </tr>
  );
};

interface TagRowProps {
  row: FlatTagRow;
  isCollapsed: boolean;
  activeDrag: ActiveTagDrag | null;
  parentOptions: { value: string; name: string }[];
  // eslint-disable-next-line no-unused-vars
  onToggleCollapse: (groupId: number) => void;
  // eslint-disable-next-line no-unused-vars
  onTogglePin: (target: GalleryTagGroup) => void;
  // eslint-disable-next-line no-unused-vars
  onRenameCommit: (target: GalleryTagGroup, newName: string) => void;
  // eslint-disable-next-line no-unused-vars
  onAliasesCommit: (target: GalleryTagGroup, aliases: string[]) => void;
  // eslint-disable-next-line no-unused-vars
  onParentChange: (target: GalleryTagGroup, rawValue: string) => void;
  // eslint-disable-next-line no-unused-vars
  onRequestDelete: (target: GalleryTagGroup) => void;
}

/**
 * タグ 1 行（ドラッグ元 + 「子にする」ドロップ先 + セル内直接編集）。
 */
const TagRow: React.FC<TagRowProps> = ({
  row,
  isCollapsed,
  activeDrag,
  parentOptions,
  onToggleCollapse,
  onTogglePin,
  onRenameCommit,
  onAliasesCommit,
  onParentChange,
  onRequestDelete,
}) => {
  const { group, depth, hasChildren } = row;

  // ドラッグ元（ハンドルのみ listeners を付ける）
  const dragData = useMemo(() => ({ group }), [group]);
  const {
    setNodeRef: setDragRef,
    listeners,
    attributes,
    isDragging,
  } = useDraggable({ id: `tag:${group.id}`, data: dragData });

  // ドロップ先（この行の子にする）。自分自身と配下は循環になるため無効
  const validDropTarget =
    activeDrag !== null && !activeDrag.excludedIds.has(group.id);
  const rowDropData = useMemo<RowDropData>(
    () => ({ type: "row", groupId: group.id }),
    [group.id],
  );
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `tag-drop:${group.id}`,
    data: rowDropData,
    disabled: !validDropTarget,
  });

  // ===== マスタータグ名（クリックで編集、blur / Enter で保存） =====
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(group.name);
  useEffect(() => {
    if (!isEditingName) setNameDraft(group.name);
  }, [group.name, isEditingName]);

  const commitName = () => {
    setIsEditingName(false);
    const trimmed = nameDraft.trim();
    if (trimmed === group.name) return;
    onRenameCommit(group, trimmed);
  };
  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsEditingName(false);
      setNameDraft(group.name);
    }
  };

  // ===== エイリアス（追加/削除のたびに即保存。反映待ちの間はドラフト表示） =====
  const [aliasesDraft, setAliasesDraft] = useState<string[]>(() =>
    group.aliases.map((a) => a.name),
  );
  useEffect(() => {
    setAliasesDraft(group.aliases.map((a) => a.name));
  }, [group.aliases]);

  const handleAliasesChange = (next: string[]) => {
    setAliasesDraft(next);
    onAliasesCommit(group, next);
  };

  const rowStyle: React.CSSProperties = {
    opacity: isDragging ? 0.4 : 1,
    ...(validDropTarget
      ? {
          background: isOver ? "rgba(0, 154, 206, 0.10)" : undefined,
          boxShadow: isOver
            ? "inset 0 0 0 2px var(--soremo-blue)"
            : "inset 0 0 0 1px rgba(0, 154, 206, 0.3)",
        }
      : {}),
  };

  return (
    <tr
      ref={(el) => {
        setDragRef(el);
        setDropRef(el);
      }}
      style={rowStyle}
    >
      {/* ドラッグハンドル */}
      <td style={{ textAlign: "center" }}>
        <span
          {...attributes}
          {...listeners}
          aria-label="並び替え・階層移動"
          className="material-symbols-rounded u-text-light-gray"
          style={{
            fontSize: 18,
            cursor: "grab",
            touchAction: "none",
            display: "inline-flex",
          }}
        >
          drag_indicator
        </span>
      </td>

      {/* ピン留め ON/OFF */}
      <td style={{ textAlign: "center" }}>
        <IconButton
          icon="push_pin"
          size={18}
          filled={group.is_pinned}
          color={
            group.is_pinned ? "var(--soremo-blue)" : "var(--soremo-border)"
          }
          onClick={() => onTogglePin(group)}
          ariaLabel={group.is_pinned ? "ピン留めを解除" : "ピン留め"}
          title={
            group.is_pinned
              ? "ピン留め中（クリックで解除）"
              : "初期タグクラウドの先頭にピン留め"
          }
        />
      </td>

      {/* マスタータグ名（インデント + チェブロン + インライン編集） */}
      <td>
        <div
          className="u-row u-items-center"
          style={{ gap: 2, paddingLeft: depth * INDENT_PER_DEPTH }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => onToggleCollapse(group.id)}
              aria-label={isCollapsed ? "展開" : "折りたたみ"}
              aria-expanded={!isCollapsed}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
                padding: 0,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <span
                className="material-symbols-rounded u-text-light-gray"
                style={{ fontSize: 18 }}
              >
                {isCollapsed ? "chevron_right" : "expand_more"}
              </span>
            </button>
          ) : (
            <span style={{ width: 20, flexShrink: 0 }} />
          )}
          {isEditingName ? (
            <input
              type="text"
              autoFocus
              value={nameDraft}
              maxLength={60}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={commitName}
              onKeyDown={handleNameKeyDown}
              style={{ ...NAME_INPUT_STYLE, flex: 1, minWidth: 0 }}
            />
          ) : (
            /* 見切れたときだけ全文を出す（見えている内容と同じツールチップは出さない） */
            <ToolTip description={group.name} truncatedOnly>
              <div
                className="bodytext-13"
                onClick={() => {
                  setNameDraft(group.name);
                  setIsEditingName(true);
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 22,
                  lineHeight: "22px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: "text",
                }}
              >
                {group.name}
              </div>
            </ToolTip>
          )}
        </div>
      </td>

      {/* 親タグ（フォルダ）。D&D のほかセレクタでも変更できる */}
      <td>
        <Select
          size="small"
          name={`parent-${group.id}`}
          value={group.parent_id !== null ? String(group.parent_id) : ""}
          options={parentOptions}
          onChange={(e) => onParentChange(group, e.target.value)}
        />
      </td>

      {/* エイリアス（セル内で直接編集、即保存） */}
      <td>
        <ContentTagInput
          value={aliasesDraft}
          onChange={handleAliasesChange}
          placeholder="同義語を追加"
          variant="bare"
        />
      </td>

      {/* 件数 = 作品数 + 肩書き一致ユーザー数（親タグは配下の子タグ分も含む） */}
      <td style={{ textAlign: "right" }}>
        <span
          className="bodytext-13"
          title={hasChildren ? "配下の子タグの分も含む" : undefined}
        >
          {group.works_count}
        </span>
      </td>

      {/* 削除 */}
      <td style={{ textAlign: "center" }}>
        <IconButton
          icon="delete"
          size={18}
          onClick={() => onRequestDelete(group)}
          ariaLabel="マスタータグを削除"
          title="削除"
        />
      </td>
    </tr>
  );
};

// =====================================================================
// AdminGalleryTags 本体
// =====================================================================

/**
 * GALLERY タグ統合管理ページ（/console/gallery-tags）。
 *
 * 「作品登録」（MySiteWorks）と同じ部品構成の表形式 UI:
 * - グローバル `Table` の行にマスタータグをツリー表示（深さ × 16px の
 *   インデント + チェブロンで開閉）。名前・親タグ・エイリアスはセル内で
 *   直接編集（即保存）
 * - 行頭ハンドルの D&D はファイルブラウザと同じ操作感:
 *   行の上にドロップ = その行の子にする（階層移動）、行間のインジケータに
 *   ドロップ = そのレベルで並び替え。`reorder` API へ
 *   `{id, parent_id, display_order}` を一括送信して確定する
 *   （ピン留めタグはこの並び順で GALLERY 初期タグクラウドの先頭に出る）
 * - ピンはアイコンの ON/OFF（ON = 塗りつぶし青）
 * - 親タグ（フォルダ）の階層は自由な深さ（BE の安全弁として 6 段まで。
 *   循環参照は BE が 400 で拒否）。親タグでの検索は配下の子タグ
 *   （孫以深も含む）の作品まで含み、子タグでの検索はその範囲だけに絞られる
 * - 「未割当タグ」は公開作品で実際に使われているタグに加え、肩書き・
 *   アーティスト名・作品名からの派生候補（source 付き）。クリックで
 *   新規マスタータグ化、または追加先に選んだタグのエイリアスにできる
 *
 * タグ自体（作品側の HashTag）は書き換えない非破壊マッピングのため、
 * グループを削除すれば元の表示に戻る（親を削除しても子は最上位に昇格）。
 * 権限は管理者（ADMIN）とマネージャー（MANAGER）。
 */
const AdminGalleryTags: React.FC = () => {
  const { data: currentUser, isLoading: isUserLoading } =
    useGetCurrentUserQuery();

  const { data: groupsData, isLoading: isGroupsLoading } =
    useGetGalleryTagGroupsQuery();
  const groups = groupsData?.data.groups;
  const unmappedTags = groupsData?.data.unmapped_tags ?? [];

  const [createGroup, { isLoading: isCreating }] =
    useCreateGalleryTagGroupMutation();
  const [updateGroup] = useUpdateGalleryTagGroupMutation();
  const [deleteGroup, { isLoading: isDeleting }] =
    useDeleteGalleryTagGroupMutation();
  const [reorderGroups] = useReorderGalleryTagGroupsMutation();

  // ===== ツリー（走査順の flat 配列。D&D 中はローカルが先行し、保存後に同期） =====
  const [localGroups, setLocalGroups] = useState<GalleryTagGroup[]>([]);
  useEffect(() => {
    setLocalGroups(toTraversalOrder(groups ?? []));
  }, [groups]);

  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());
  const handleToggleCollapse = useCallback((groupId: number) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const childrenMap = useMemo(() => buildChildrenMap(localGroups), [localGroups]);
  const visibleRows = useMemo(
    () => flattenVisibleRows(localGroups, collapsedIds),
    [localGroups, collapsedIds],
  );

  /** 祖先を辿ったパス表示名（例: "MUSIC › CityPop"）。 */
  const pathLabelById = useMemo(() => {
    const byId = new Map(localGroups.map((g) => [g.id, g]));
    const labels = new Map<number, string>();
    const labelOf = (group: GalleryTagGroup, trail: Set<number>): string => {
      const cached = labels.get(group.id);
      if (cached !== undefined) return cached;
      let label = group.name;
      if (group.parent_id !== null && !trail.has(group.id)) {
        const parent = byId.get(group.parent_id);
        if (parent) {
          trail.add(group.id);
          label = `${labelOf(parent, trail)} › ${group.name}`;
          trail.delete(group.id);
        }
      }
      labels.set(group.id, label);
      return label;
    };
    localGroups.forEach((g) => labelOf(g, new Set()));
    return labels;
  }, [localGroups]);

  /** 親タグ Select の選択肢（自分自身と配下の子孫は循環になるため除外）。 */
  const parentOptionsFor = useCallback(
    (group: GalleryTagGroup) => {
      const excluded = collectSubtreeIds(group.id, childrenMap);
      return [
        { value: "", name: "なし（最上位）" },
        ...localGroups
          .filter((g) => !excluded.has(g.id))
          .map((g) => ({
            value: String(g.id),
            name: pathLabelById.get(g.id) ?? g.name,
          })),
      ];
    },
    [localGroups, childrenMap, pathLabelById],
  );

  // ===== 新規作成（表示順は BE が末尾に自動採番） =====
  const [newName, setNewName] = useState("");

  const createMasterTag = useCallback(
    async (name: string): Promise<boolean> => {
      try {
        await createGroup({ name }).unwrap();
        toast.success(`マスタータグ「${name}」を作成しました`);
        return true;
      } catch (err: unknown) {
        toast.error(errorMessage(err, "マスタータグの作成に失敗しました"));
        return false;
      }
    },
    [createGroup],
  );

  const handleCreate = useCallback(async () => {
    const name = newName.trim();
    if (!name || isCreating) return;
    if (await createMasterTag(name)) {
      setNewName("");
    }
  }, [newName, isCreating, createMasterTag]);

  // ===== セル編集 =====
  const handleTogglePin = useCallback(
    async (group: GalleryTagGroup) => {
      try {
        await updateGroup({
          id: group.id,
          is_pinned: !group.is_pinned,
        }).unwrap();
      } catch (err: unknown) {
        toast.error(errorMessage(err, "ピン留めの更新に失敗しました"));
      }
    },
    [updateGroup],
  );

  const handleRenameCommit = useCallback(
    async (group: GalleryTagGroup, newGroupName: string) => {
      if (!newGroupName) {
        toast.error("マスタータグ名は必須です");
        return;
      }
      try {
        await updateGroup({ id: group.id, name: newGroupName }).unwrap();
      } catch (err: unknown) {
        toast.error(errorMessage(err, "名前の変更に失敗しました"));
      }
    },
    [updateGroup],
  );

  const handleAliasesCommit = useCallback(
    async (group: GalleryTagGroup, nextAliases: string[]) => {
      try {
        await updateGroup({ id: group.id, aliases: nextAliases }).unwrap();
      } catch (err: unknown) {
        toast.error(errorMessage(err, "エイリアスの更新に失敗しました"));
      }
    },
    [updateGroup],
  );

  const handleParentChange = useCallback(
    async (group: GalleryTagGroup, raw: string) => {
      const nextParentId = raw ? Number(raw) : null;
      if (nextParentId === group.parent_id) return;
      try {
        await updateGroup({ id: group.id, parent_id: nextParentId }).unwrap();
      } catch (err: unknown) {
        toast.error(errorMessage(err, "親タグの変更に失敗しました"));
      }
    },
    [updateGroup],
  );

  // ===== 行 D&D（行の上 = 子にする / 行間 = 並び替え） =====
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const [activeDrag, setActiveDrag] = useState<ActiveTagDrag | null>(null);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const group = (
        event.active.data.current as { group?: GalleryTagGroup } | undefined
      )?.group;
      if (!group) return;
      setActiveDrag({
        group,
        excludedIds: collectSubtreeIds(group.id, childrenMap),
      });
    },
    [childrenMap],
  );

  /** 行間インジケータは薄いので、ポインタ位置優先で当てる。 */
  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const byPointer = pointerWithin(args);
    return byPointer.length > 0 ? byPointer : rectIntersection(args);
  }, []);

  const handleRowDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveDrag(null);
      const { active, over } = event;
      if (!over) return;
      const dragged = (
        active.data.current as { group?: GalleryTagGroup } | undefined
      )?.group;
      const dropData = over.data.current as TagDropData | undefined;
      if (!dragged || !dropData) return;

      let moved: GalleryTagGroup[] | null;
      if (dropData.type === "row") {
        // 行の上へドロップ = その行の子（末尾）にする
        moved = applyTreeMove(localGroups, dragged.id, dropData.groupId, null);
        // 折りたたまれた行に落としたら展開して結果を見せる
        setCollapsedIds((prev) => {
          if (!prev.has(dropData.groupId)) return prev;
          const next = new Set(prev);
          next.delete(dropData.groupId);
          return next;
        });
      } else {
        moved = applyTreeMove(
          localGroups,
          dragged.id,
          dropData.parentId,
          dropData.beforeId,
        );
      }
      if (moved === null) {
        toast("自分の配下へは移動できません");
        return;
      }
      // 変化なし（同じ位置へ落とした）なら API を呼ばない
      const unchanged =
        moved.length === localGroups.length &&
        moved.every(
          (g, i) =>
            g.id === localGroups[i].id &&
            g.parent_id === localGroups[i].parent_id,
        );
      if (unchanged) return;

      setLocalGroups(moved);
      try {
        await reorderGroups(
          moved.map((g, index) => ({
            id: g.id,
            parent_id: g.parent_id,
            display_order: index,
          })),
        ).unwrap();
      } catch (err: unknown) {
        toast.error(errorMessage(err, "移動に失敗しました"));
        setLocalGroups(toTraversalOrder(groups ?? []));
      }
    },
    [localGroups, reorderGroups, groups],
  );

  // ===== 未割当タグのクリック時の追加先（"new" = 新規マスタータグ化） =====
  const [unmappedTarget, setUnmappedTarget] = useState("new");

  // ===== 削除 =====
  const [deleteTarget, setDeleteTarget] = useState<GalleryTagGroup | null>(
    null,
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteGroup(deleteTarget.id).unwrap();
      toast.success(`マスタータグ「${deleteTarget.name}」を削除しました`);
      setUnmappedTarget((cur) =>
        cur === String(deleteTarget.id) ? "new" : cur,
      );
    } catch (err: unknown) {
      toast.error(errorMessage(err, "マスタータグの削除に失敗しました"));
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteGroup]);

  // ===== 未割当タグ（クリックで新規作成 or エイリアス追加） =====
  const unmappedTargetOptions = useMemo(
    () => [
      { value: "new", name: "新しいマスタータグとして作成" },
      ...localGroups.map((g) => ({
        value: String(g.id),
        name: `${pathLabelById.get(g.id) ?? g.name} のエイリアスに追加`,
      })),
    ],
    [localGroups, pathLabelById],
  );

  const handleUnmappedClick = useCallback(
    async (tagName: string) => {
      if (unmappedTarget === "new") {
        await createMasterTag(tagName);
        return;
      }
      const target = localGroups.find((g) => String(g.id) === unmappedTarget);
      if (!target) return;
      try {
        await updateGroup({
          id: target.id,
          aliases: [...target.aliases.map((a) => a.name), tagName],
        }).unwrap();
        toast.success(
          `「${tagName}」を「${target.name}」のエイリアスに追加しました`,
        );
      } catch (err: unknown) {
        toast.error(errorMessage(err, "エイリアスの追加に失敗しました"));
      }
    },
    [unmappedTarget, localGroups, createMasterTag, updateGroup],
  );

  // ===== 権限ガード =====
  if (isUserLoading) {
    return <SvgLoadingAnimation />;
  }
  const role: string | undefined = currentUser?.data?.role;
  if (role !== "ADMIN" && role !== "MANAGER") {
    return <Navigate to="/projects" replace />;
  }

  const columns: TableColumn[] = [
    { key: "drag", width: 36, minWidth: 36, resizable: false },
    {
      key: "pin",
      header: "ピン",
      width: 48,
      minWidth: 40,
      resizable: false,
      headerTitle:
        "ピン留めしたタグは作品 0 件でも初期タグクラウドの先頭に、表の並び順どおりに表示されます",
    },
    { key: "name", header: "マスタータグ", minWidth: 180 },
    { key: "parent", header: "親タグ", width: 170, minWidth: 110 },
    {
      key: "aliases",
      header: "エイリアス（同義語）",
      width: 300,
      minWidth: 180,
    },
    {
      key: "works",
      header: "件数",
      width: 72,
      minWidth: 56,
      headerTitle:
        "該当する公開作品数 + 肩書き一致ユーザー数（親タグは配下の子タグ分も含む）",
    },
    { key: "delete", width: 40, minWidth: 36, resizable: false },
  ];
  const columnCount = columns.length;

  const deleteHasChildren =
    deleteTarget !== null &&
    (childrenMap.get(deleteTarget.id) ?? []).length > 0;

  /** ドラッグ中のみ行間へ差し込む挿入インジケータの definition を組み立てる。 */
  const gapBefore = (row: FlatTagRow, index: number) => (
    <GapDropRow
      key={`gap-${index}`}
      gapId={`gap:${index}`}
      parentId={row.group.parent_id}
      beforeId={row.group.id}
      depth={row.depth}
      disabled={
        activeDrag !== null &&
        row.group.parent_id !== null &&
        activeDrag.excludedIds.has(row.group.parent_id)
      }
      columnCount={columnCount}
    />
  );

  return (
    <PageWrapper>
      {/* 説明は ? アイコンのホバーに畳む（本題の表を上に詰める） */}
      <div className="u-row-between u-items-start u-w100 u-gap8">
        <TitleBlock title="GALLERY タグ統合" />
        <HelpHint description={"同じ意味で表記だけが違うタグ（例: NA / ナレーション / ナレ）をマスタータグに束ねます。名前・親タグ・エイリアスはセルで直接編集できます。行のドラッグで、行の上に重ねればその子タグに（フォルダのように階層移動）、行間の線に合わせれば並び替えになります。親タグでの検索は配下の子タグ（孫以深も含む）の作品まで含み、子タグでの検索はその範囲だけに絞られます。作品側のタグは書き換えないため、削除すれば元に戻ります。"} />
      </div>

      {/* ===== 1. マスタータグ一覧（ツリー表形式 + D&D） ===== */}
      <Section>
        <form
          className="u-row u-items-center u-gap8"
          style={{ flexWrap: "wrap" }}
          onSubmit={(e) => {
            e.preventDefault();
            void handleCreate();
          }}
        >
          <Input
            width="240px"
            placeholder="新しいマスタータグ名"
            value={newName}
            maxlength={60}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Button
            size="small"
            theme="primary"
            variant="filled"
            leftIcon="new_label"
            onClick={() => void handleCreate()}
            disabled={!newName.trim() || isCreating}
          >
            {isCreating ? "作成中..." : "追加"}
          </Button>
        </form>

        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragEnd={(e) => void handleRowDragEnd(e)}
          onDragCancel={() => setActiveDrag(null)}
        >
          <Table columns={columns} minWidth={760}>
            {isGroupsLoading ? (
              <tr>
                <td colSpan={columnCount}>
                  <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                    読み込み中...
                  </p>
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td colSpan={columnCount}>
                  <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                    マスタータグはまだありません。上の入力欄から作成するか、下の「未割当タグ」をクリックしてください。
                  </p>
                </td>
              </tr>
            ) : (
              <>
                {visibleRows.map((row, index) => (
                  <React.Fragment key={row.group.id}>
                    {activeDrag !== null && gapBefore(row, index)}
                    <TagRow
                      row={row}
                      isCollapsed={collapsedIds.has(row.group.id)}
                      activeDrag={
                        activeDrag?.group.id === row.group.id
                          ? null
                          : activeDrag
                      }
                      parentOptions={parentOptionsFor(row.group)}
                      onToggleCollapse={handleToggleCollapse}
                      onTogglePin={(g) => void handleTogglePin(g)}
                      onRenameCommit={(g, name) =>
                        void handleRenameCommit(g, name)
                      }
                      onAliasesCommit={(g, aliases) =>
                        void handleAliasesCommit(g, aliases)
                      }
                      onParentChange={(g, raw) =>
                        void handleParentChange(g, raw)
                      }
                      onRequestDelete={(g) => setDeleteTarget(g)}
                    />
                  </React.Fragment>
                ))}
                {/* 末尾のギャップ = 最上位レベルの末尾へ移動 */}
                {activeDrag !== null && (
                  <GapDropRow
                    gapId="gap:end"
                    parentId={null}
                    beforeId={null}
                    depth={0}
                    disabled={false}
                    columnCount={columnCount}
                  />
                )}
              </>
            )}
          </Table>

          {/* D&D 中にカーソルへ追従するゴースト */}
          <DragOverlay dropAnimation={null}>
            {activeDrag && (
              <DragGhost>
                <span className="material-symbols-rounded">sell</span>
                <span>{activeDrag.group.name}</span>
              </DragGhost>
            )}
          </DragOverlay>
        </DndContext>
      </Section>

      {/* ===== 2. 未割当タグ ===== */}
      <Section>
        <div
          className="u-row-between u-items-center u-w100"
          style={{ flexWrap: "wrap", gap: 8 }}
        >
          <h2 className="heading-13">未割当タグ</h2>
          <span className="u-row u-items-center u-gap8">
            <span className="bodytext-11 u-text-light-gray">
              クリック時の動作
            </span>
            <span style={{ width: 280 }}>
              <Select
                size="small"
                name="unmapped-target"
                value={unmappedTarget}
                options={unmappedTargetOptions}
                onChange={(e) => setUnmappedTarget(e.target.value)}
              />
            </span>
          </span>
        </div>
        <p className="bodytext-11 u-text-light-gray">
          公開作品で実際に使われているタグと、肩書き・アーティスト名・作品名からの派生候補のうち、どのマスタータグにも割り当てられていないもの（件数順。肩書きは該当ユーザー数）。
        </p>
        {isGroupsLoading ? (
          <SvgLoadingAnimation />
        ) : unmappedTags.length === 0 ? (
          <p className="bodytext-13 u-text-light-gray">
            未割当のタグはありません。
          </p>
        ) : (
          <TagChipList>
            {unmappedTags.map((tag) => {
              const sourceMeta = UNMAPPED_SOURCE_META[tag.source];
              return (
                <UnmappedChip
                  key={tag.name}
                  type="button"
                  onClick={() => void handleUnmappedClick(tag.name)}
                  title={
                    sourceMeta
                      ? `${sourceMeta.label}由来のタグ候補`
                      : "作品の実タグ"
                  }
                >
                  {sourceMeta && (
                    <span
                      className="material-symbols-rounded u-text-light-gray"
                      style={{ fontSize: 14 }}
                    >
                      {sourceMeta.icon}
                    </span>
                  )}
                  <span className="bodytext-13">{tag.name}</span>
                  <span className="bodytext-11 u-text-light-gray">
                    {tag.works_count}
                  </span>
                </UnmappedChip>
              );
            })}
          </TagChipList>
        )}
      </Section>

      {deleteTarget && (
        <ConfirmModal
          message={`マスタータグ「${deleteTarget.name}」を削除しますか？\nエイリアスの統合が解除され、GALLERY では元のタグ表記がそのまま表示されるようになります（作品のタグ自体は消えません）。${
            deleteHasChildren
              ? "\n配下の子タグは削除されず、最上位のマスタータグになります。"
              : ""
          }`}
          confirmLabel="削除"
          onConfirm={() => void handleDelete()}
          onCancel={() => setDeleteTarget(null)}
          loading={isDeleting}
        />
      )}
    </PageWrapper>
  );
};

export default AdminGalleryTags;
